<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import ScrollNav from '$lib/Components/ScrollNav/ScrollNav.svelte';

	import headerImg from './images/LP-Installation-Guide-01.jpg';
	import cutWithScissors from './images/LP-Installation-Guide-02.png';
	import wrongCutting1 from './images/LP-Installation-Guide-03.png';
	import wrongCutting2 from './images/LP-Installation-Guide-04.png';
	import handleCorrect1 from './images/LP-Installation-Guide-05.jpg';
	import handleCorrect2 from './images/LP-Installation-Guide-06.jpg';
	import handleWrong1 from './images/LP-Installation-Guide-07.jpg';
	import handleWrong2 from './images/LP-Installation-Guide-08.jpg';
	import hardwareEnclosed from './images/LP-Installation-Guide-09.jpg';
	import img10 from './images/LP-Installation-Guide-10.jpg';
	import installBrackets from './images/LP-Installation-Guide-11.png';
	import measureDistance from './images/LP-Installation-Guide-12.png';
	import drillAndPlaceBracket from './images/LP-Installation-Guide-13.png';
	import filmRemoval from './images/LP-Installation-Guide-14.jpg';
	import placeBottom from './images/LP-Installation-Guide-15.jpg';
	import attachSafetyChains from './images/LP-Installation-Guide-16.jpg';
	import pressAndFix from './images/LP-Installation-Guide-17.jpg';
	import checkTilt from './images/LP-Installation-Guide-18.png';
	import checkCompression from './images/LP-Installation-Guide-19.png';
	import removeAndReinstall from './images/LP-Installation-Guide-20.png';
</script>

<Section backgroundImage={headerImg} backgroundColor="blue">
	<h1>How To Install IndowONE Inserts</h1>
</Section>

<Section backgroundColor="grey-light">
	<h2>CUTTING THE PACKAGING</h2>
	<p>
		Carefully cut and remove the cardboard and plastic wrap from your Indow
		inserts, making sure to not cut into the cardboard itself or jab or slice
		the acrylic. Use scissors, <strong>not box cutters or blades</strong>.
	</p>
	<p>
		<strong>NOTE:</strong> All cardboard packaging can be recycled.
	</p>

	<ul
		class="
    grid list-none grid-cols-3 gap-6
    [&_img]:max-w-48
  "
	>
		<li>
			<Image src={cutWithScissors} loading="eager">
				{#snippet descriptionSlot()}
					<p class="text-center font-bold text-indow-green!">YES</p>
				{/snippet}
			</Image>
		</li>
		<li>
			<Image src={wrongCutting1} loading="eager">
				{#snippet descriptionSlot()}
					<p class="text-center text-indow-red!">NO</p>
				{/snippet}
			</Image>
		</li>
		<li>
			<Image src={wrongCutting2} loading="eager">
				{#snippet descriptionSlot()}
					<p class="text-center text-indow-red!">NO</p>
				{/snippet}
			</Image>
		</li>
	</ul>
</Section>

<ScrollNav>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#handling-once-unpacked" class="scrollactive-item"
			>HANDLING ONCE UNPACKED</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#install-indow-inserts" class="scrollactive-item"
			>INSTALL THE INDOW INSERTS</a
		>
	</li>
	<li>
		<!-- eslint-disable-next-line better-tailwindcss/no-unknown-classes -->
		<a href="#check-your-work" class="scrollactive-item">CHECK YOUR WORK</a>
	</li>
</ScrollNav>

<Section id="handling-once-unpacked">
	<h2>Handling Once Unpacked</h2>
	<p>
		Handling Once Unpacked Make sure the Indow insert stays mostly straight, and
		does not bend or bow. If carrying with two people, hold the insert
		vertically, perpendicular to floor so panel does not bend/bow.
	</p>

	<div
		class="
    grid grid-flow-row gap-4
    md:grid-flow-col
  "
	>
		<div>
			<Image src={handleCorrect1}>
				{#snippet descriptionSlot()}
					<p class="font-bold text-indow-green!">YES</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={handleCorrect2}>
				{#snippet descriptionSlot()}
					<p class="font-bold text-indow-green!">YES</p>
				{/snippet}
			</Image>
		</div>
	</div>
	<p>
		Do not place hands directly across from each other on a rectangular insert,
		or carry by holding the compression tube only.
	</p>
	<div
		class="
    grid grid-flow-row gap-4
    md:grid-flow-col
  "
	>
		<div>
			<Image src={handleWrong1}>
				{#snippet descriptionSlot()}
					<p class="font-bold text-indow-red!">NO</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={handleWrong2}>
				{#snippet descriptionSlot()}
					<p>NO</p>
				{/snippet}
			</Image>
		</div>
	</div>
</Section>

<Section>
	<h2>REMOVE PACKAGING</h2>
	<p>
		<strong>Frame Brackets</strong> may be required. Keep an eye out for a small
		package with an orange label that says <strong>“HARDWARE ENCLOSED”</strong>.
		If frame brackets are required they will be in this package along with the
		<strong>Welcome Kit</strong>.
	</p>
	<Image src={hardwareEnclosed} />
	<h2>TEST FIT & INSTALL HARDWARE</h2>
	<p>
		Ensure the Indow insert is paired with the correct opening. You can find the
		room name and location on the product label AND/OR etched into the back side
		of the Compression Tube in the lower left-hand corner.
	</p>
	<Image src={img10} class="w-full" />
	<p>
		Determine the location for the Indow insert within the frame i.e. <strong
			>'Frame Step'</strong
		>, as noted on the product label.
	</p>
	<p>
		Test fit the Indow insert to ensure proper fit before peeling <strong
			>THE PROTECTIVE FILM</strong
		>.
	</p>
	<p>
		<strong>NOTE:</strong> The insert will be slightly larger than the opening to
		allow for a compression fit.
	</p>
	<p>
		Install the safety chain frame brackets in frames (only required for larger
		inserts if bracket location on product label reads N/A brackets are not
		required).
	</p>
</Section>

<Section>
	<div
		class="
    grid grid-flow-row gap-4
    md:grid-flow-col
    [&_img]:max-w-48
  "
	>
		<div>
			<Image src={installBrackets}>
				{#snippet descriptionSlot()}
					<p>
						You will be installing TWO brackets – one on each side of the frame.
					</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={measureDistance}>
				{#snippet descriptionSlot()}
					<p>
						Measure the noted distance from the bottom of the frame up and mark
						location with pencil. NOTE: The bracket should be installed
						immediately behind where the Indow insert will sit in the frame so
						that it is hidden behind the insert.
					</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={drillAndPlaceBracket}>
				{#snippet descriptionSlot()}
					<p>
						Pre-drill, then install bracket with screw provided. We suggest
						using a 1/16” drill bit.
					</p>
				{/snippet}
			</Image>
		</div>
	</div>
</Section>

<Section
	title="Install The Indow Insert"
	id="install-indow-inserts"
	class="[&_img]:w-full"
>
	<p>
		Remove the film from the <strong>BACKSIDE</strong> (i.e., the side facing toward
		the existing window). The back of the panel has the insert label, unfinished corners
		and safety chains, and will face the window glass when installed. Wipe down the
		backside with the microfiber cloth.
	</p>
	<Image src={filmRemoval} />
	<p>
		<strong>NOTE:</strong> When cleaning your Indow inserts,
		<strong>DO NOT USE ANY</strong> ammonia, alcohol, or bleach-based cleaners such
		as Windex, Clorox or 409. Do not use lemon juice or vinegar. Do not use paper
		towels, newspapers or rough cloths. Use only microfiber cleaning cloths (See Indow
		Care Guide for more information).
	</p>
	<Image src={placeBottom} />
	<p>
		Place the bottom of the insert on your window sill and within the desired
		frame step. Nudge the bottom of the insert into place and make sure the
		bottom corners are evenly compressed (i.e. not positioned too far over to
		the right or left).
	</p>
	<Image src={attachSafetyChains} />
	<p>
		Reach around both sides of the Indow inserts and attach the safety chains to
		the brackets. Place the last ball of the ball-chain into the bracket. With
		the Indow insert tilting slightly toward you, grab sides of the insert and
		gently bend the top of the insert toward the frame and press the top edge
		into the frame.
	</p>
	<Image src={pressAndFix} />
	<p>
		Work your way down the sides, pressing the compression tube into place. If
		there is no stop right behind the compression tube, make sure the sides are
		straight and not wavy.
	</p>
</Section>

<Section id="check-your-work">
	<h2>Check Your Work</h2>
	<div
		class="
    grid grid-flow-row gap-4
    md:grid-flow-col
    [&_img]:max-w-48
  "
	>
		<div>
			<Image src={checkTilt}>
				{#snippet descriptionSlot()}
					<p>
						Make sure the Indow insert is straight and not tilting vertically or
						skewed horizontally in the frame.
					</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={checkCompression}>
				{#snippet descriptionSlot()}
					<p>
						Ensure the insert is sitting well in the frame – not overly
						compressed, nor any gaps or loose fitting areas. Pinch the tubing to
						make minor positioning adjustments.
					</p>
				{/snippet}
			</Image>
		</div>
		<div>
			<Image src={removeAndReinstall}>
				{#snippet descriptionSlot()}
					<p>
						If you notice any gaps or overly compressed areas, remove the insert
						using the D-ring and reinstall fallowing the instructions above.
					</p>
				{/snippet}
			</Image>
		</div>
	</div>
	<p>
		Peel remaining film from the front side of the insert and wipe clean with
		microfiber cleaning cloth (see cleaning note in step 6, or review Indow Care
		Guide). Visually check all Indow inserts for any defects – any defects
		should be reported to your representative immediately (see the Receiving &
		Handling Guide for what to look for, and how to report damage).
	</p>
</Section>
